"use client"

import type { ReactNode } from "react"
import Image from "next/image"
import Link from "next/link"
import { buttonVariants } from "@/components/ui/button"
import { blobMediaUrl } from "@/lib/blob-url"

/**
 * Sections shared by `/insurance` and the per-insurer pages
 * (`/insurance/<insurer>`). Every string comes in as a prop: the callers keep
 * their `t(k.*)` calls so `scripts/markdown-mirror-coverage.ts` still sees
 * which keys each page renders.
 */

/* ── Progressive blur over the closing-CTA photo (tuned 2026-08-18): the
   blur ramps from 0 at 31% of the section height to ~16px at 92%, so the
   photo softens behind the copy and stays sharp at the very bottom edge. ── */
const CTA_BLUR_LAYERS = [
  [31, 41],
  [41, 51],
  [51, 62],
  [62, 72],
  [72, 82],
  [82, 92],
] as const
const CTA_BLUR_PER_LAYER_PX = 2.7

/* Progressive blur over the lower part of the hero photo (tuned 2026-08-17):
   six stacked backdrop-filter layers, each masked to start a little further
   down than the previous one, so the blur ramps from 0 at 40% of the hero
   height to the full ~30px at the bottom edge where the card overlaps. */
const HERO_BLUR_LAYERS = [
  [40, 50],
  [50, 60],
  [60, 70],
  [70, 80],
  [80, 90],
  [90, 100],
] as const
const HERO_BLUR_PER_LAYER_PX = 5

/* The journey step markers, kept as the original cut-out discs: one path per
   step where the numeral is a hole in the circle. Inlined (rather than the
   step-N.svg files they came from) so the disc can take a brand colour via
   `currentColor` — a plain image element can't be recoloured. The white
   circle underneath fills the hole, so the numeral reads white on a card of
   any shade. */
const JOURNEY_STEP_PATHS = [
  "M 16 0 C 7.163 0 0 7.163 0 16 C 0 24.837 7.163 32 16 32 C 24.837 32 32 24.837 32 16 C 31.991 7.167 24.833 0.009 16 0 Z M 17.846 23.385 C 17.846 24.064 17.295 24.615 16.615 24.615 C 15.936 24.615 15.385 24.064 15.385 23.385 L 15.385 10.923 L 13.606 12.109 C 13.04 12.486 12.276 12.334 11.898 11.768 C 11.521 11.202 11.674 10.437 12.24 10.06 L 15.932 7.598 C 16.309 7.347 16.793 7.323 17.193 7.535 C 17.593 7.748 17.844 8.163 17.846 8.615 Z",
  "M 16 0 C 7.163 0 0 7.163 0 16 C 0 24.837 7.163 32 16 32 C 24.837 32 32 24.837 32 16 C 31.991 7.167 24.833 0.009 16 0 Z M 19.692 22.154 C 20.372 22.154 20.923 22.705 20.923 23.385 C 20.923 24.064 20.372 24.615 19.692 24.615 L 12.308 24.615 C 11.842 24.615 11.415 24.352 11.207 23.935 C 10.998 23.518 11.043 23.019 11.323 22.646 L 17.965 13.791 C 18.546 13.017 18.619 11.974 18.152 11.126 C 17.685 10.278 16.765 9.783 15.8 9.86 C 14.835 9.937 14.005 10.572 13.678 11.483 C 13.452 12.124 12.748 12.46 12.107 12.233 C 11.466 12.006 11.13 11.303 11.357 10.662 C 12.006 8.836 13.665 7.56 15.596 7.403 C 17.527 7.245 19.371 8.235 20.307 9.932 C 21.243 11.629 21.096 13.717 19.932 15.266 L 14.769 22.154 Z",
  "M 16 0 C 7.163 0 0 7.163 0 16 C 0 24.837 7.163 32 16 32 C 24.837 32 32 24.837 32 16 C 31.991 7.167 24.833 0.009 16 0 Z M 15.385 25.231 C 13.895 25.235 12.468 24.635 11.429 23.568 C 11.102 23.258 10.97 22.794 11.085 22.358 C 11.201 21.922 11.546 21.585 11.984 21.478 C 12.422 21.371 12.883 21.512 13.186 21.846 C 14.164 22.843 15.69 23.057 16.904 22.367 C 18.118 21.677 18.715 20.257 18.358 18.907 C 18.002 17.557 16.781 16.616 15.385 16.615 C 14.926 16.615 14.505 16.36 14.293 15.953 C 14.081 15.546 14.114 15.054 14.377 14.678 L 17.328 10.462 L 12.308 10.462 C 11.628 10.462 11.077 9.911 11.077 9.231 C 11.077 8.551 11.628 8 12.308 8 L 19.692 8 C 20.151 8 20.572 8.256 20.784 8.663 C 20.996 9.07 20.963 9.561 20.7 9.937 L 17.469 14.552 C 19.936 15.542 21.352 18.145 20.842 20.754 C 20.333 23.362 18.043 25.241 15.385 25.231 Z",
  "M 12.363 18.462 L 17.231 12.203 L 17.231 18.462 Z M 32 16 C 32 24.837 24.837 32 16 32 C 7.163 32 0 24.837 0 16 C 0 7.163 7.163 0 16 0 C 24.833 0.009 31.991 7.167 32 16 Z M 22.154 19.692 C 22.154 19.013 21.603 18.462 20.923 18.462 L 19.692 18.462 L 19.692 8.615 C 19.692 8.09 19.358 7.623 18.861 7.452 C 18.364 7.282 17.814 7.446 17.491 7.86 L 8.875 18.937 C 8.587 19.308 8.535 19.811 8.741 20.233 C 8.948 20.655 9.376 20.923 9.846 20.923 L 17.231 20.923 L 17.231 23.385 C 17.231 24.064 17.782 24.615 18.462 24.615 C 19.141 24.615 19.692 24.064 19.692 23.385 L 19.692 20.923 L 20.923 20.923 C 21.603 20.923 22.154 20.372 22.154 19.692 Z",
] as const

const JOURNEY_STEP_COLOURS = [
  "text-bright-green-600",
  "text-bright-blue-600",
  "text-aubergine-600",
  "text-terracotta-400",
] as const

function JourneyStepMark({ step }: { step: number }) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      className={JOURNEY_STEP_COLOURS[step % 4]}
      aria-hidden="true"
    >
      <circle cx="16" cy="16" r="16" fill="#fff" />
      <path d={JOURNEY_STEP_PATHS[step % 4]} fill="currentColor" />
    </svg>
  )
}

/**
 * The hero subtitle plus a link to the reimbursement terms: the promise in
 * the subtitle ("we pay you the difference") names its conditions there.
 * Both insurance routes are CH-only, as are the terms.
 */
export function SubtitleWithTermsLink({
  text,
  linkLabel,
  href,
}: {
  text: string
  linkLabel: string
  href: string
}) {
  return (
    <>
      {text}{" "}
      <Link
        href={href}
        className="underline underline-offset-2 hover:text-neutral-900"
      >
        {linkLabel}
      </Link>
    </>
  )
}

/**
 * Photo hero — the heading IS the checker's heading. Pulled up behind the
 * sticky navbar (ambassadors pattern: the navbar's white/70 glass sits over
 * the photo), lightened top-down so the copy stays legible, and tall enough
 * that the white checker card overlaps its lower part and starts above the
 * fold. The eyebrow is optional: the per-insurer pages run heading plus one
 * paragraph, with nothing above the H1.
 *
 * The `/go/insurance*` ad LPs keep their own campaign photo: they pass
 * `imageSrc` with `theme="dark"`, which swaps the lightening wash for a dark
 * scrim and sets the copy in white.
 */
export function InsurancePhotoHero({
  label,
  heading,
  subtitle,
  imageSrc = blobMediaUrl("/images/insurance/hero-alpine-meadow.jpg"),
  imagePosition = "60% 63%",
  imageShiftX = 0,
  theme = "light",
}: {
  label?: string
  heading: string
  subtitle: ReactNode
  imageSrc?: string
  /** CSS object-position for the photo. */
  imagePosition?: string
  /** Moves the photo sideways by this share of its width, negative to the
   *  left, for a photo as wide as the hero, where object-position cannot. It
   *  zooms in just enough that no edge shows. */
  imageShiftX?: number
  /** "dark" = dark scrim + white copy, for photos the light wash can't carry. */
  theme?: "light" | "dark"
}) {
  const isDark = theme === "dark"
  return (
    <section className="relative -mt-[72px] overflow-hidden pt-[112px] pb-44 text-center md:pt-[112px] md:pb-60">
      <Image
        src={imageSrc}
        alt=""
        fill
        priority
        // `priority` alone only emits the `<link rel="preload">`; as of Next 16
        // it no longer implies `fetchpriority`, so this LCP hero was queued at
        // Chrome's default (Low) priority behind the route scripts and the
        // ~14 insurer-logo image preloads — a measured 2.1s LCP load delay on
        // mobile. `fetchPriority` puts it in the high-priority queue, matching
        // `preloadHero()` on the homepage and the immunity hero.
        fetchPriority="high"
        sizes="100vw"
        aria-hidden="true"
        className="origin-top scale-[1.04] object-cover"
        style={{
          objectPosition: imagePosition,
          ...(imageShiftX
            ? {
                translate: `${imageShiftX}% 0`,
                scale: `${1.04 + (2 * Math.abs(imageShiftX)) / 100}`,
              }
            : {}),
        }}
      />
      {/* The blur is part of the meadow treatment: on a campaign photo it
          smears the people the photo is about, so the dark theme skips it. */}
      {!isDark &&
        HERO_BLUR_LAYERS.map(([from, to]) => {
          const mask = `linear-gradient(to bottom, transparent ${from}%, black ${to}%)`
          return (
            <div
              key={from}
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                backdropFilter: `blur(${HERO_BLUR_PER_LAYER_PX}px)`,
                WebkitBackdropFilter: `blur(${HERO_BLUR_PER_LAYER_PX}px)`,
                maskImage: mask,
                WebkitMaskImage: mask,
              }}
            />
          )
        })}
      {isDark ? (
        /* Dark scrim, strongest behind the navbar and the centred copy, so
           the white text reads over a campaign photo. */
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.2) 100%)",
          }}
        />
      ) : (
        /* Same lightening overlay as the ambassadors hero, so the dark
           copy reads over the photo. */
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(121.09deg, rgb(224, 236, 242) 22.654%, rgba(255, 255, 255, 0) 49.505%), linear-gradient(180deg, rgba(215, 236, 251, 0.67) 0%, rgba(255, 255, 255, 0) 62.453%)",
            opacity: 0.85,
          }}
        />
      )}
      {/* Soft wash into the page background at the bottom edge, so the
          photo hands over to the grey page under the card. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(246,246,246,0) 40%, rgba(246,246,246,1) 100%)",
        }}
      />
      <div className="relative mx-auto max-w-[1440px] px-4 md:px-10">
        {/* Desktop-only where it's used at all: on a phone it costs a line
            of the first screen without telling the reader anything the H1
            doesn't. */}
        {label && (
          <p
            className={`type-label-lg hidden md:block ${isDark ? "text-white/80" : "text-neutral-600"}`}
          >
            {label}
          </p>
        )}
        <h1
          className={`type-display-lg mx-auto max-w-3xl text-balance md:mt-4 ${isDark ? "text-white" : "text-neutral-900"}`}
        >
          {heading}
        </h1>
        <p
          className={`type-body-xl mx-auto mt-4 max-w-2xl text-pretty ${isDark ? "text-white/85" : "text-neutral-700"}`}
        >
          {subtitle}
        </p>
      </div>
    </section>
  )
}

export interface JourneyStep {
  title: string
  description: string
}

/** "Your journey to preventive healthcare gets easier from here" — dark band, four cards. */
export function InsuranceJourneySection({
  heading,
  steps,
}: {
  heading: string
  steps: readonly JourneyStep[]
}) {
  return (
    <section className="bg-neutral-50 pt-24 pb-12 md:pt-[100px] md:pb-[100px]">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <h2 className="type-display-sm text-center text-neutral-900">
          {heading}
        </h2>
        <div className="mt-8 grid grid-cols-2 gap-3 md:mt-10 md:gap-6 lg:grid-cols-4">
          {steps.map((step, i) => (
            <div
              key={step.title}
              className="flex h-full flex-col gap-4 rounded-[16px] bg-white p-5 md:gap-6 md:p-10"
            >
              <JourneyStepMark step={i} />
              <div>
                <h3 className="type-title-md md:type-title-lg text-neutral-900">
                  {step.title}
                </h3>
                <p className="type-body-sm md:type-body-md mt-2 text-neutral-600">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/** KVG / VVG explainer — two photo cards in a masonry pair. */
export function InsuranceTypesSection({
  heading,
  basicTitle,
  basicDescription,
  basicBadge,
  supplementaryTitle,
  supplementaryDescription,
  supplementaryBadge,
}: {
  heading: string
  basicTitle: string
  basicDescription: string
  /** Abbreviation on the decorative badge — "KVG" in DE/EN, "LAMal" in FR. */
  basicBadge: string
  supplementaryTitle: string
  supplementaryDescription: string
  /** Abbreviation on the decorative badge — "VVG+" in DE/EN, "LCA" in FR. */
  supplementaryBadge: string
}) {
  return (
    <section className="py-16">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* White panel, same treatment as the journey cards above: the KVG/VVG
            explainer reads as one block on the page grey rather than floating
            on it. */}
        {/* Taller than it is wide-padded, and the top is 12px short of the bottom
            on purpose: the heading's glyphs start ~11px below its line box, so
            equal padding reads top-heavy. */}
        <div className="rounded-[16px] bg-white px-6 pt-13 pb-16 md:px-12 md:pt-23 md:pb-26">
          <h2 className="type-display-sm mx-auto max-w-[828px] text-center text-balance text-neutral-900">
            {heading}
          </h2>

          <div className="mx-auto mt-10 grid max-w-[828px] grid-cols-1 gap-4 md:grid-cols-2">
            {/* Col 1 */}
            <div className="flex flex-col gap-4">
              <div>
                <div className="relative h-[320px] overflow-hidden rounded-[16px] md:h-[421px]">
                  <Image
                    src={blobMediaUrl("/images/insurance/basic-insurance.jpeg")}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="(min-width: 768px) 394px, 100vw"
                    quality={90}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute right-0 bottom-0 left-0 flex flex-col justify-end gap-4 p-7">
                    <h3 className="type-headline-md text-white">
                      {basicTitle}
                    </h3>
                    <p className="type-body-lg text-white/80">
                      {basicDescription}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <div className="hidden items-center justify-center gap-4 rounded-[16px] bg-neutral-100 px-7 py-8 md:flex">
                  <span className="type-display-sm leading-[1.2] font-light text-neutral-500">
                    {basicBadge}
                  </span>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/icons/kvg-figures.svg"
                    alt=""
                    width={49}
                    height={39}
                  />
                </div>
              </div>
            </div>

            {/* Col 2 */}
            <div className="flex flex-col gap-4">
              <div>
                <div className="hidden items-center justify-center gap-4 rounded-[16px] bg-neutral-100 px-7 py-8 md:flex">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/icons/vvg-shield.svg"
                    alt=""
                    width={25}
                    height={32}
                  />
                  <span className="type-display-sm leading-[1.2] font-light text-neutral-500">
                    {supplementaryBadge}
                  </span>
                </div>
              </div>
              <div>
                <div className="relative h-[320px] overflow-hidden rounded-[16px] md:h-[421px]">
                  <Image
                    src={blobMediaUrl(
                      "/images/insurance/supplementary-insurance.png"
                    )}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="(min-width: 768px) 394px, 100vw"
                    quality={90}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute right-0 bottom-0 left-0 flex flex-col justify-end gap-4 p-7">
                    <h3 className="type-headline-md text-white">
                      {supplementaryTitle}
                    </h3>
                    <p className="type-body-lg text-white/80">
                      {supplementaryDescription}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/** Closing CTA — the metabolism LP's treatment: full-bleed photo, dark scrim,
 *  white centred copy, booking CTA + contact link. */
export function InsuranceClosingCta({
  heading,
  subtitle,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
}: {
  heading: string
  subtitle: string
  primaryLabel: string
  primaryHref: string
  secondaryLabel: string
  secondaryHref: string
}) {
  return (
    <section className="relative isolate flex min-h-[520px] items-center overflow-hidden text-white md:min-h-[620px]">
      <Image
        src={blobMediaUrl("/images/metabolism/closing-cta-metabolism.webp")}
        alt=""
        aria-hidden
        fill
        sizes="100vw"
        className="object-cover object-center"
      />
      {CTA_BLUR_LAYERS.map(([from, to]) => {
        const mask = `linear-gradient(to bottom, transparent ${from}%, black ${to}%)`
        return (
          <div
            key={from}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backdropFilter: `blur(${CTA_BLUR_PER_LAYER_PX}px)`,
              WebkitBackdropFilter: `blur(${CTA_BLUR_PER_LAYER_PX}px)`,
              maskImage: mask,
              WebkitMaskImage: mask,
            }}
          />
        )
      })}
      {/* dark scrim keeps the white copy legible over the photo */}
      <div aria-hidden className="absolute inset-0 bg-neutral-950/55" />
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-20 text-center md:px-10 md:py-28">
        <h2 className="type-display-md mx-auto max-w-[680px] text-white">
          {heading}
        </h2>
        <p className="type-body-lg mx-auto mt-5 max-w-[520px] text-white/70">
          {subtitle}
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
          <Link
            href={primaryHref}
            className={`${buttonVariants({ variant: "primary" })} w-full justify-center sm:w-auto`}
          >
            {primaryLabel}
          </Link>
          <Link
            href={secondaryHref}
            className={`${buttonVariants({ variant: "outline" })} w-full justify-center border-white/40 text-white hover:border-white/60 hover:bg-white/10 sm:w-auto`}
          >
            {secondaryLabel}
          </Link>
        </div>
      </div>
    </section>
  )
}
